/**
 * What the page remembers — per person **per device**, on the server
 * (`docs/activity.md` §4.6; Aaron, D6).
 *
 * Read once when the page opens (`GET /activity/state`); every change is
 * applied at once on screen and `PATCH`ed — only what changed, debounced
 * 600 ms — so two quick clicks are one request and nothing waits on the
 * network. The server merges per key and drops people this person may no
 * longer see, so the answer it gives back is the one to trust.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { mergeState, type ActivityState, type StoredActivityState } from '@rowted/shared'
import { activityRequest } from './api.js'

const DEBOUNCE_MS = 600

export interface ActivityStateHandle {
  state: ActivityState | null
  /**
   * Changes what the page remembers: on screen now, on the server in 600 ms —
   * or at once with `now`, for a change made as the page is about to be left
   * (Navigate handing the phone to the maps app).
   */
  change: (patch: StoredActivityState, options?: { now?: boolean }) => void
  error: string | null
}

/** Applies a patch to a whole state the way the server will. */
function applied(state: ActivityState, patch: StoredActivityState): ActivityState {
  return mergeState(state as StoredActivityState, patch) as ActivityState
}

export function useActivityState(): ActivityStateHandle {
  const [state, setState] = useState<ActivityState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pending = useRef<StoredActivityState>({})
  const timer = useRef<number | null>(null)

  useEffect(() => {
    let live = true
    activityRequest<{ state: ActivityState }>('/activity/state')
      .then((answer) => { if (live) setState(answer.state) })
      .catch((cause: unknown) => { if (live) setError(cause instanceof Error ? cause.message : String(cause)) })
    return () => { live = false }
  }, [])

  const flush = useCallback((keepalive = false) => {
    timer.current = null
    const patch = pending.current
    pending.current = {}
    if (Object.keys(patch).length === 0) return
    void activityRequest('/activity/state', { method: 'PATCH', body: patch, keepalive }).catch(() => {
      /* What the page remembers is a convenience; a failed save is tried with the next change. */
      pending.current = mergeState(patch, pending.current)
    })
  }, [])

  const change = useCallback((patch: StoredActivityState, options?: { now?: boolean }) => {
    setState((current) => (current ? applied(current, patch) : current))
    pending.current = mergeState(pending.current, patch)
    if (timer.current !== null) window.clearTimeout(timer.current)
    if (options?.now) flush(true)
    else timer.current = window.setTimeout(() => flush(), DEBOUNCE_MS)
  }, [flush])

  /* Leaving the page sends what is still waiting. */
  useEffect(() => () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      flush()
    }
  }, [flush])

  return { state, change, error }
}
